
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler } from "react-hook-form";

const appointmentSchema = z.object({
  doctor_name: z.string().min(2, "Le nom du médecin doit contenir au moins 2 caractères"),
  specialty: z.string().optional(),
  location: z.string().min(5, "L'adresse doit contenir au moins 5 caractères"),
  date: z.string().refine((date) => {
    const selectedDate = new Date(date);
    const now = new Date();
    return selectedDate > now;
  }, "La date doit être dans le futur"),
  duration: z.string().optional(),
  reason: z.string().optional(),
  insurance_info: z.string().optional(),
  documents_required: z.string().optional(),
  notes: z.string().optional(),
});

export type FormValues = z.infer<typeof appointmentSchema>;

export const useAppointmentForm = (onOpenChange: (open: boolean) => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      doctor_name: "",
      specialty: "",
      location: "",
      date: new Date().toISOString().split('T')[0],
      duration: "",
      reason: "",
      insurance_info: "",
      documents_required: "",
      notes: "",
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Erreur d'authentification",
          description: "Vous devez être connecté pour ajouter un rendez-vous",
        });
        return;
      }

      const { error } = await supabase
        .from("appointments")
        .insert({
          doctor_name: values.doctor_name,
          specialty: values.specialty,
          location: values.location,
          date: values.date,
          duration: values.duration,
          reason: values.reason,
          insurance_info: values.insurance_info,
          documents_required: values.documents_required,
          notes: values.notes,
          user_id: user.id,
        });

      if (error) {
        console.error("Error inserting appointment:", error);
        throw error;
      }

      toast({
        title: "Rendez-vous ajouté",
        description: "Votre rendez-vous a été planifié avec succès",
        variant: "default",
      });
      
      toast({
        title: "Rappel configuré",
        description: `Vous recevrez un rappel avant votre rendez-vous avec ${values.doctor_name}`,
        variant: "default",
      });
      
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'ajouter le rendez-vous. Veuillez réessayer.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    onSubmit,
  };
};
